import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { getApi } from "../../../Services/apiService";
import { getMemberDobTimestamp } from "../../../Utils/family-tree/dateAndTime";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  isDashboardStartOrTopOfLineMember,
  isExplicitDashboardStartOrTopOfLineMember,
} from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthSuperscriptText,
  getMultipleBirthLegendTags,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  formatDashboardStartTopLineFullName,
  getDashboardMemberLineColor,
  getDashboardMemberNameParts,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardLineTotalsPopup, {
  buildDashboardLineTotals,
} from "./DashboardLineTotalsPopup";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import {
  applyDashboardMemberFilterToggle,
  filterDashboardMembersByMemberFilter,
  getDashboardBaseSortForMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";
import {
  buildDashboardFamilyLineNumberMap,
  getDashboardFamilyLineNumber,
  sortDashboardMembersByAgeWithInLawsAlphabetical,
  sortDashboardMembersByOldestFamilyOrder,
  sortDashboardMembersByFamilyLineNumberOrder,
  sortDashboardMembersByDeathDate,
  applyDashboardInLawsLastOrder,
} from "../../../Utils/dashboardFamilyLineNumbers";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

function TotalMaleFemale() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  // Full roster, kept only to build the colour legend — see the extractor.
  const [legendMembers, setLegendMembers] = useState([]);
  const [males, setMales] = useState([]);
  const [females, setFemales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLargeView, setIsLargeView] = useState(false);
  const measureListRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const columnsWrapperRef = useRef(null);
  const maleTitleRef = useRef(null);
  const malesListRef = useRef(null);
  // { male, female } — each column sized to its own longest name on desktop, so
  // the gap before the line column matches on both sides.
  const [nameColumnWidth, setNameColumnWidth] = useState(null);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // Fetch male and female members from API
  useEffect(() => {
    const fetchMaleFemaleMembers = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setMales([]);
          setFemales([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              extractAllMembersFromStatisticsResponse(response.data)
            )
          );
          setLegendMembers(extractAllMembersFromStatisticsResponse(response.data));
          // Extract male and female members from the response
          // The API returns: data.total_male_female.males and data.total_male_female.females
          const maleFemaleData = response?.data?.total_male_female || {};

          const malesData = maleFemaleData?.males || [];
          const femalesData = maleFemaleData?.females || [];
          
          // Ensure they are arrays
          if (Array.isArray(malesData)) {
            setMales(malesData);
          } else {
            console.warn("Males data is not an array:", malesData);
            setMales([]);
          }
          
          if (Array.isArray(femalesData)) {
            setFemales(femalesData);
          } else {
            console.warn("Females data is not an array:", femalesData);
            setFemales([]);
          }
        } else {
          setMales([]);
          setFemales([]);
        }
      } catch (error) {
        console.error("Error fetching male/female members:", error);
        setMales([]);
        setFemales([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMaleFemaleMembers();
  }, []);

  // The page opens on no tab at all: every member is listed, the couple at the
  // head of each line included, so the report reads as the whole tree until a
  // tab narrows it. Descendants is one of those tabs now rather than the view
  // the page starts in. The sort side opens unlit to match.
  const [sortBy, setSortBy] = useState("none"); // "none", "alpha-asc", "alpha-desc", "youngest", "oldest", "color", "death-asc"
  // Picking the Line Color sort opens the per-line totals popup, split by gender.
  const [showLineTotals, setShowLineTotals] = useState(false);
  const handleSortChange = (next) => {
    setSortBy(next);
    setShowLineTotals(next === "color");
  };
  const closeLineTotals = useCallback(() => setShowLineTotals(false), []);
  const [memberFilter, setMemberFilter] = useState("all"); // shared dashboard member filters
  const [searchQuery, setSearchQuery] = useState("");

  // A member tab on the right of the bar's divider is selected on its own: it
  // clears every sort on the left, and the list is ordered by the tab's own
  // order below without a sort button lit for it.
  //
  // The sort side effect runs here in the click handler, not inside a
  // setMemberFilter updater: updaters run in the render phase, where React does not
  // reliably apply a setState for another piece of state — the list sorted but the
  // sort bar never lit the matching button up.
  const toggleMemberFilter = (filter) => {
    setMemberFilter(
      applyDashboardMemberFilterToggle(memberFilter, filter, setSortBy)
    );
  };

  // What the list is actually ordered by. With no sort lit, that is the order
  // the active member tab is read in — the bar stays unlit either way.
  const effectiveSort =
    sortBy === "none" ? getDashboardBaseSortForMemberFilter(memberFilter) : sortBy;

  const allMembersForNumbering = useMemo(
    () => [...males, ...females],
    [males, females]
  );

  const lineNumberMap = useMemo(
    () => buildDashboardFamilyLineNumberMap(allMembersForNumbering),
    [allMembersForNumbering]
  );

  const formatPersonName = (member) => {
    if (
      memberFilter === "start-top" &&
      isDashboardStartOrTopOfLineMember(member)
    ) {
      return formatDashboardStartTopLineFullName(member);
    }
    return formatDashboardMemberDisplayName(member, { appendDescendantSpouse: false });
  };

  // The member tab narrows each column first, then the search box narrows it
  // again, so both column counts and the heading read the rows on screen.
  const filteredMales = useMemo(
    () =>
      filterDashboardMembersBySearch(
        filterDashboardMembersByMemberFilter(males, memberFilter),
        searchQuery,
        formatPersonName
      ),
    [males, memberFilter, searchQuery]
  );

  const filteredFemales = useMemo(
    () =>
      filterDashboardMembersBySearch(
        filterDashboardMembersByMemberFilter(females, memberFilter),
        searchQuery,
        formatPersonName
      ),
    [females, memberFilter, searchQuery]
  );

  // Sort males and females based on sortBy (after in-law / descendants filter)
  const sortedMales = useMemo(() => {
    if (!filteredMales.length) return filteredMales;
    if (effectiveSort === "none") {
      // No tab picked: the whole tree is listed, the couple at the head of each
      // line first, then the descendants in line-number order and the in-laws
      // A–Z at the end — the order Family Members opens in.
      return sortDashboardMembersByFamilyLineNumberOrder(
        filteredMales,
        memberFilter,
        // Same name options the rows render with, so the A–Z run of in-laws
        // matches the names actually on screen.
        { lineNumberMap, nameOptions: { appendDescendantSpouse: false } }
      );
    }

    if (effectiveSort === "death-asc") {
      return sortDashboardMembersByDeathDate(filteredMales, {
        appendDescendantSpouse: false,
      });
    }

    // In-laws are not counted in anything involving age, so the age sorts rank
    // the descendants only and leave any in-laws on screen in A–Z order.
    if (effectiveSort === "oldest") {
      return sortDashboardMembersByAgeWithInLawsAlphabetical(
        filteredMales,
        (list) => sortDashboardMembersByOldestFamilyOrder(list, memberFilter)
      );
    }

    // A–Z and Z–A read the whole list the tab is showing — in-laws sit in the
    // run with everyone else instead of being parked at the end of it. Only the
    // two age sorts park them, and that is because they are not ranked by age.
    if (effectiveSort === "alpha-asc") {
      return [...filteredMales].sort((a, b) => {
        const nameA = formatPersonName(a).toLowerCase();
        const nameB = formatPersonName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    if (effectiveSort === "alpha-desc") {
      return [...filteredMales].sort((a, b) => {
        const nameA = formatPersonName(a).toLowerCase();
        const nameB = formatPersonName(b).toLowerCase();
        return nameB.localeCompare(nameA);
      });
    }

    if (effectiveSort === "youngest") {
      return sortDashboardMembersByAgeWithInLawsAlphabetical(
        filteredMales,
        (list) =>
          [...list].sort((a, b) => {
            const dobA = getMemberDobTimestamp(a);
            const dobB = getMemberDobTimestamp(b);

            if (dobA === -1 && dobB === -1) return 0;
            if (dobA === -1) return 1;
            if (dobB === -1) return -1;
            return dobB - dobA;
          })
      );
    }

    if (effectiveSort === "color") {
      // Include in-laws in the same line/color groups (do not park them at the end).
      return [...filteredMales].sort((a, b) => {
        const colorA = (a?.color_name || "").toLowerCase();
        const colorB = (b?.color_name || "").toLowerCase();
        if (colorA === colorB) {
          const nameA = formatPersonName(a).toLowerCase();
          const nameB = formatPersonName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        }
        return colorA.localeCompare(colorB);
      });
    }

    return applyDashboardInLawsLastOrder(filteredMales, memberFilter);
  }, [filteredMales, effectiveSort, memberFilter, lineNumberMap]);

  const sortedFemales = useMemo(() => {
    if (!filteredFemales.length) return filteredFemales;
    if (effectiveSort === "none") {
      // No tab picked: the whole tree is listed, the couple at the head of each
      // line first, then the descendants in line-number order and the in-laws
      // A–Z at the end — the order Family Members opens in.
      return sortDashboardMembersByFamilyLineNumberOrder(
        filteredFemales,
        memberFilter,
        // Same name options the rows render with, so the A–Z run of in-laws
        // matches the names actually on screen.
        { lineNumberMap, nameOptions: { appendDescendantSpouse: false } }
      );
    }

    if (effectiveSort === "death-asc") {
      return sortDashboardMembersByDeathDate(filteredFemales, {
        appendDescendantSpouse: false,
      });
    }

    // In-laws are not counted in anything involving age, so the age sorts rank
    // the descendants only and leave any in-laws on screen in A–Z order.
    if (effectiveSort === "oldest") {
      return sortDashboardMembersByAgeWithInLawsAlphabetical(
        filteredFemales,
        (list) => sortDashboardMembersByOldestFamilyOrder(list, memberFilter)
      );
    }

    // A–Z and Z–A read the whole list the tab is showing — in-laws sit in the
    // run with everyone else instead of being parked at the end of it. Only the
    // two age sorts park them, and that is because they are not ranked by age.
    if (effectiveSort === "alpha-asc") {
      return [...filteredFemales].sort((a, b) => {
        const nameA = formatPersonName(a).toLowerCase();
        const nameB = formatPersonName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    if (effectiveSort === "alpha-desc") {
      return [...filteredFemales].sort((a, b) => {
        const nameA = formatPersonName(a).toLowerCase();
        const nameB = formatPersonName(b).toLowerCase();
        return nameB.localeCompare(nameA);
      });
    }

    if (effectiveSort === "youngest") {
      return sortDashboardMembersByAgeWithInLawsAlphabetical(
        filteredFemales,
        (list) =>
          [...list].sort((a, b) => {
            const dobA = getMemberDobTimestamp(a);
            const dobB = getMemberDobTimestamp(b);

            if (dobA === -1 && dobB === -1) return 0;
            if (dobA === -1) return 1;
            if (dobB === -1) return -1;
            return dobB - dobA;
          })
      );
    }

    if (effectiveSort === "color") {
      // Include in-laws in the same line/color groups (do not park them at the end).
      return [...filteredFemales].sort((a, b) => {
        const colorA = (a?.color_name || "").toLowerCase();
        const colorB = (b?.color_name || "").toLowerCase();
        if (colorA === colorB) {
          const nameA = formatPersonName(a).toLowerCase();
          const nameB = formatPersonName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        }
        return colorA.localeCompare(colorB);
      });
    }

    return applyDashboardInLawsLastOrder(filteredFemales, memberFilter);
  }, [filteredFemales, effectiveSort, memberFilter, lineNumberMap]);

  const showMaleLineNumberColumn = useMemo(
    () =>
      sortedMales.some(
        (member) => getDashboardFamilyLineNumber(member, lineNumberMap) != null
      ),
    [sortedMales, lineNumberMap]
  );

  const showFemaleLineNumberColumn = useMemo(
    () =>
      sortedFemales.some(
        (member) => getDashboardFamilyLineNumber(member, lineNumberMap) != null
      ),
    [sortedFemales, lineNumberMap]
  );

  useLayoutEffect(() => {
    if (loading || (sortedMales.length === 0 && sortedFemales.length === 0)) {
      setNameColumnWidth(null);
      return;
    }

    const measureEl = measureListRef.current;
    if (!measureEl) return;

    const maxNameWidth = (selector) => {
      let maxWidth = 0;
      measureEl.querySelectorAll(selector).forEach((col) => {
        maxWidth = Math.max(maxWidth, col.getBoundingClientRect().width);
      });
      return Math.ceil(maxWidth);
    };

    const measureNameColumnWidth = () => {
      const male = maxNameWidth("[data-gender='male'] .dashboard-detail-name-col");
      const female = maxNameWidth("[data-gender='female'] .dashboard-detail-name-col");
      if (male === 0 && female === 0) return;
      // Phones stack both groups in one grid, so they keep one shared width there
      // and the line column stays aligned from the males into the females.
      if (isCompactLayout) {
        const shared = Math.max(male, female);
        setNameColumnWidth({ male: shared, female: shared });
      } else {
        setNameColumnWidth({ male, female });
      }
    };

    measureNameColumnWidth();

    const ro = new ResizeObserver(measureNameColumnWidth);
    ro.observe(measureEl);
    window.addEventListener("resize", measureNameColumnWidth);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureNameColumnWidth);
    };
  }, [
    sortedMales,
    sortedFemales,
    loading,
    isLargeView,
    isCompactLayout,
    memberFilter,
    sortBy,
    showMaleLineNumberColumn,
    showFemaleLineNumberColumn,
  ]);

  // Phones stack the two groups in one scroller, so opening at the very top fills
  // the screen with males and gives no sign that a Females list follows. Open on
  // the join instead — the last male row parked under the sticky "Males" title,
  // the Females list starting right below it — so both groups are on screen the
  // moment the report opens, on every member tab including Start/Top of Line.
  //
  // Re-anchored only when the rows themselves change (tab, sort, search, the
  // measured name width that moves them), never on a scroll, so whatever the
  // reader scrolls to afterwards is left alone.
  useLayoutEffect(() => {
    if (!isCompactLayout || loading) return;
    // Nothing to land on when either group is empty — the one list on screen is
    // already the whole view.
    if (!sortedMales.length || !sortedFemales.length) return;

    const wrapper = columnsWrapperRef.current;
    const listEl = malesListRef.current;
    const titleEl = maleTitleRef.current;
    if (!wrapper || !listEl || !titleEl) return;

    const anchorToLastMaleRow = () => {
      // Rows are `display: contents` here, so the grid's own children are the
      // cells; the last row's name cell is the one to park under the title.
      const nameCells = listEl.querySelectorAll(".dashboard-detail-name-col");
      const lastNameCell = nameCells[nameCells.length - 1];
      if (!lastNameCell) return;
      // Measured against the scroller itself rather than through offsetTop, so
      // the sum holds whatever the rows are nested in at this width.
      const offsetFromTop =
        lastNameCell.getBoundingClientRect().top - wrapper.getBoundingClientRect().top;
      wrapper.scrollTop = Math.max(
        0,
        wrapper.scrollTop + offsetFromTop - titleEl.offsetHeight
      );
    };

    anchorToLastMaleRow();
    // Column widths and photos can settle a frame later and carry the join with
    // them, so take the measurement again once that has landed.
    const frame = requestAnimationFrame(anchorToLastMaleRow);

    return () => cancelAnimationFrame(frame);
  }, [
    isCompactLayout,
    loading,
    sortedMales,
    sortedFemales,
    nameColumnWidth,
    memberFilter,
    sortBy,
    searchQuery,
  ]);

  // `applyMeasuredWidth` is false for the hidden measuring list only: feeding the
  // measured width back into the rows it is measured from would latch the column
  // at whatever the first filter needed, so a later filter with longer names could
  // never widen it.
  const renderMemberRow = (member, showLineNumberColumn, gender, applyMeasuredWidth = true) => {
    const { baseName, descendantSpouseName } = getDashboardMemberNameParts(member, {
      appendDescendantSpouse: false,
    });
    const lineNumber = getDashboardFamilyLineNumber(member, lineNumberMap);
    const isDeceased = member?.present_or_deceased === 2;
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    // Listed in-laws never get a line number, so a numbered in-law is the Start/Top
    // couple the numbering already counts — this payload ships no `generation` to
    // prove it explicitly.
    const showInLawAsterisk =
      isDashboardInLawMember(member) &&
      !isExplicitDashboardStartOrTopOfLineMember(member) &&
      lineNumber == null;
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(member, headsMap);
    const hexCode = member?.hex_code || "#000000";
    const colorName = member?.color_name || "";

    const iconSize = isLargeView ? "18px" : "12px";
    const measuredWidth = nameColumnWidth?.[gender];

    return (
      <div className={`dashboard-detail-row ${isLargeView ? "large-view" : ""}`}>
        <div
          className={`dashboard-detail-name-col ${isLargeView ? "large-view" : ""}${
            !showLineNumberColumn ? " dashboard-detail-name-col--no-line-number" : ""
          }`}
          style={
            measuredWidth && applyMeasuredWidth
              ? {
                  width: `${measuredWidth}px`,
                  minWidth: `${measuredWidth}px`,
                }
              : undefined
          }
        >
          {showLineNumberColumn && (
            <span className="dashboard-family-line-number" aria-hidden={lineNumber == null}>
              {lineNumber ?? ""}
            </span>
          )}
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...getDashboardMemberNamePhotoUrls(member)}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={baseName}
                descendantSpouseName={descendantSpouseName}
                afterBaseName={
                  showInLawAsterisk && descendantSpouseName ? (
                    <DashboardInLawAsterisk className="total-male-female-page in-law-asterisk" isLargeView={isLargeView} />
                  ) : null
                }
              />
            </DashboardPhotoName>
            {showInLawAsterisk && !descendantSpouseName && (
              <DashboardInLawAsterisk className="total-male-female-page in-law-asterisk" isLargeView={isLargeView} />
            )}
            {isDeceased && (
              <span className={`dashboard-detail-angel-icon ${isLargeView ? "large-view" : ""}`}>
                <img
                  src={window.getPublicUrl("/assets/images/angel.png")}
                  alt="deceased"
                  style={{ width: iconSize, height: iconSize }}
                />
              </span>
            )}
            {multipleBirthSup && (
              <sup
                className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}
              >
                {multipleBirthSup}
              </sup>
            )}
          </DashboardNameMarkerGroup>
        </div>

        {/* Compact drops the head-of-line name and prints the line colour in its own
            colour; the footer legend ties each colour to its head */}
        {isCompactLayout ? (
          <div className="dashboard-detail-line-col dashboard-detail-line-col--compact">
            <DashboardLineColorName colorName={colorName} hexCode={hexCode} />
          </div>
        ) : (
          <div
            className={`dashboard-detail-line-col ${isLargeView ? "large-view-text" : ""}`}
          >
            <DashboardPhotoHeadLine
              name={headOfLineName || "Start/Top of Line"}
              hexCode={hexCode}
              colorName={colorName ? `${colorName} Line` : ""}
              imageUrl={getDashboardMemberPhotoUrl(member, "head_image")}
              onViewPhoto={openPhoto}
              member={member}
              headsMap={headsMap}
            />
          </div>
        )}
      </div>
    );
  };

  const rawTotalCount = males.length + females.length;
  const totalCount = filteredMales.length + filteredFemales.length;
  const maleCount = filteredMales.length;
  const femaleCount = filteredFemales.length;

  // Label for the active view — it rides the heading line beside the back arrow.
  const filterLabel =
    memberFilter === "in-laws"
      ? "In-Laws (Living)"
      : memberFilter === "in-laws-deceased"
        ? "In-Laws (Deceased)"
        : memberFilter === "in-relationship"
          ? "In Relationship"
          : memberFilter === "descendants"
            ? "Descendants"
            : memberFilter === "descendants-living"
              ? "Descendants (Living)"
              : memberFilter === "descendants-deceased"
                ? "Descendants (Deceased)"
                : memberFilter === "start-top"
                  ? "Start/Top of Line"
                  : "";
  // Singular when a column holds exactly one person — "Male (1)" reads wrong as
  // "Males (1)". Zero keeps the plural ("Males (0)").
  const maleLabel = maleCount === 1 ? "Male" : "Males";
  const femaleLabel = femaleCount === 1 ? "Female" : "Females";

  // The heading names the two groups, matching the "Males (111)" / "Females (71)"
  // titles over the columns below it. "Descendants" is abbreviated here only; the
  // filter button it comes from still spells it out. With no tab picked the
  // heading carries no suffix — the page is listing everyone.
  const headingFilterLabel = filterLabel.replace(/Descendants/g, "Desc");
  const listTitle = headingFilterLabel
    ? `${maleLabel}/${femaleLabel} — ${headingFilterLabel}`
    : `${maleLabel}/${femaleLabel}`;

  // Built from the whole tree, not this page's rows, so the footer legend lists the
  // same family lines here as on every other dashboard list. Built from the rows it
  // only showed lines this page happened to contain.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(legendMembers, headsMap),
    [legendMembers, headsMap]
  );

  const showColorLegend = isCompactLayout && legendLines.length > 0;

  // The Line Color popup: per-line totals for each column, counted off the rows
  // on screen so each list adds up to that column's count under any tab or
  // search. An in-law with no line colour of their own counts on their spouse's.
  const lineTotalSections = useMemo(() => {
    const totalsFor = (rows) =>
      buildDashboardLineTotals(
        rows.map((member) => {
          const { colorName, hexCode } = getDashboardMemberLineColor(member, legendMembers);
          return { colorName, hexCode: hexCode || "#000000" };
        }),
        legendLines
      );
    return [
      { key: "male", title: maleLabel, count: maleCount, className: "male", totals: totalsFor(filteredMales) },
      { key: "female", title: femaleLabel, count: femaleCount, className: "female", totals: totalsFor(filteredFemales) },
    ];
  }, [filteredMales, filteredFemales, legendMembers, legendLines, maleLabel, femaleLabel, maleCount, femaleCount]);

  return (
    <div className={`dashboard-container total-male-female-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
          <DashboardBackButton />
            {/* One line at every width — "Males/Females — Desc" is short enough on
                a phone that splitting it over two rows only cost height */}
            <div className="dashboard-detail-heading-label-cluster">
              <h3 className="dashboard-detail-title">{listTitle}</h3>
              <div className="dashboard-detail-count">{totalCount}</div>
            </div>
            {!loading && rawTotalCount > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
              />
            )}
            {!loading && rawTotalCount > 0 && (
              <DashboardDetailSortFilterBar
                className="dashboard-detail-sort-toggle total-male-female-sort-toggle"
                sortBy={sortBy}
                onSortChange={handleSortChange}
                memberFilter={memberFilter}
                onMemberFilterToggle={toggleMemberFilter}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading family members...</p>
          </div>
        ) : rawTotalCount === 0 ? (
          <div className="dashboard-detail-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : totalCount === 0 ? (
          <div className="dashboard-detail-empty-state">
            {searchQuery.trim() ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div
            ref={contentWrapperRef}
            className="total-male-female-page dashboard-detail-content-wrapper"
          >
            <div
              ref={columnsWrapperRef}
              className="total-male-female-page gender-columns-wrapper"
            >
              <div
                ref={measureListRef}
                className="total-male-female-page gender-list-measure"
                aria-hidden="true"
              >
                {sortedMales.map((member, index) => (
                  <div key={`measure-male-${member?.id || index}`} data-gender="male">
                    {renderMemberRow(member, showMaleLineNumberColumn, "male", false)}
                  </div>
                ))}
                {sortedFemales.map((member, index) => (
                  <div key={`measure-female-${member?.id || index}`} data-gender="female">
                    {renderMemberRow(member, showFemaleLineNumberColumn, "female", false)}
                  </div>
                ))}
              </div>
              {/* Males Column */}
              <div className="total-male-female-page gender-column gender-column-male">
                <h4
                  ref={maleTitleRef}
                  className="total-male-female-page gender-column-title male dashboard-section-heading"
                >
                  {maleLabel}{" "}<span className="dashboard-section-heading-count">({maleCount})</span>
                </h4>
                <div className="total-male-female-page gender-list-cluster">
                  <div
                    ref={malesListRef}
                    className="males-list total-male-female-page gender-list"
                  >
                    {sortedMales.length === 0 ? (
                      <p className="total-male-female-page gender-empty-text">No data available.</p>
                    ) : (
                      sortedMales.map((member, index) => (
                        <React.Fragment key={member?.id || index}>
                          {renderMemberRow(member, showMaleLineNumberColumn, "male")}
                        </React.Fragment>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Vertical Divider */}
              <div className="total-male-female-page gender-column-divider"></div>

              {/* Females Column */}
              <div className="total-male-female-page gender-column">
                <h4 className="total-male-female-page gender-column-title female dashboard-section-heading">
                  {femaleLabel}{" "}<span className="dashboard-section-heading-count">({femaleCount})</span>
                </h4>
                <div className="total-male-female-page gender-list-cluster">
                  <div className="females-list total-male-female-page gender-list">
                    {sortedFemales.length === 0 ? (
                      <p className="total-male-female-page gender-empty-text">No data available.</p>
                    ) : (
                      sortedFemales.map((member, index) => (
                        <React.Fragment key={member?.id || index}>
                          {renderMemberRow(member, showFemaleLineNumberColumn, "female")}
                        </React.Fragment>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="total-male-female-page in-law-footer">
              {/* One footer under both columns, so the key covers the tags in either */}
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags([
                  sortedMales,
                  sortedFemales,
                ])}
              />
              {showColorLegend && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </div>
        )}
      </div>
      <DashboardLineTotalsPopup
        open={showLineTotals}
        onClose={closeLineTotals}
        sections={lineTotalSections}
      />
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default TotalMaleFemale;
